// @ts-nocheck
/** Custom spell UI icons (SVG/PNG). Emoji in SPELLS[].fallbackIcon when no asset exists. */
import { SPELLS, spellInfo } from "./data";

function esc(s) {
  return String(s).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
}

/** spellId → asset path under web/ */
const SPELL_ICON_IDS = [
  "fireball", "freeze", "icelance", "scourge", "sanctuary", "frostnova", "lightning", "voidbeam", "homing", "boomerang", "chain", "chainmend",
  "splitter", "meteor", "blizzard", "bloompod", "frozenorb", "bonespear", "swap", "teleport", "thrust", "windwalk", "illusionist",
  "drain", "bouncer", "firespray", "firewall", "glacier", "rush", "shield", "timeshift", "barrier", "reflect",
  "disable", "gravity", "hook", "link", "snare", "voidrip",
  "frostspike", "voidglass_shard", "voidorb",
];

export const SPELL_ICON_ASSETS = Object.fromEntries(
  SPELL_ICON_IDS.map((id) => [id, `icons/spells/${id}.svg`]),
);

/** Synergy passive id → asset path (multishot uses the shared "multishot" key). */
export const SYNERGY_ICON_ASSETS = {
  blizzard_meteor_shower: "icons/synergies/meteor-shower.svg",
  armageddon: "icons/synergies/armageddon.svg",
  meteor_nuke: "icons/synergies/nuke.svg",
  comet: "icons/synergies/comet.svg",
  scourge_sticky_mines: "icons/synergies/sticky-mines.svg",
  frozenorb_void_maelstrom: "icons/synergies/void-maelstrom.svg",
  gravity_null_sphere: "icons/synergies/null-sphere.svg",
  glacier_voidglass: "icons/synergies/voidglass.svg",
  timeshift_bullet_time: "icons/synergies/bullet-time.svg",
  voidrip_hell_rip: "icons/synergies/hell-rip.svg",
  illusionist_suicide_squad: "icons/synergies/suicide-squad.svg",
  multishot: "icons/synergies/multishot.svg",
};

const SYNERGY_FALLBACK_ICONS = {
  blizzard_meteor_shower: "🔥",
  armageddon: "☄️",
  meteor_nuke: "☢️",
  comet: "🌠",
  scourge_sticky_mines: "🛰️",
  frozenorb_void_maelstrom: "🌌",
  gravity_null_sphere: "🕳️",
  glacier_voidglass: "🧊",
  timeshift_bullet_time: "⏱️",
  voidrip_hell_rip: "⛥",
  illusionist_suicide_squad: "💥",
  multishot: "🔱",
};

const _images = {};
const _ready = {};

export function synergyBarIconKey(synergyPassiveId) {
  if (!synergyPassiveId) return null;
  if (synergyPassiveId.endsWith("_multishot")) return "multishot";
  return synergyPassiveId;
}

export function barIconKey(spellId, synergyPassiveId) {
  return synergyBarIconKey(synergyPassiveId) || spellId;
}

export function spellHasIconAsset(spellId) {
  return Object.prototype.hasOwnProperty.call(SPELL_ICON_ASSETS, spellId);
}

export function synergyHasIconAsset(synergyPassiveId) {
  const key = synergyBarIconKey(synergyPassiveId);
  return !!key && Object.prototype.hasOwnProperty.call(SYNERGY_ICON_ASSETS, key);
}

export function barHasIconAsset(spellId, synergyPassiveId) {
  const key = barIconKey(spellId, synergyPassiveId);
  return Object.prototype.hasOwnProperty.call(SPELL_ICON_ASSETS, key) ||
    Object.prototype.hasOwnProperty.call(SYNERGY_ICON_ASSETS, key);
}

export function spellFallbackIcon(spellId) {
  const s = spellInfo(spellId);
  return s?.fallbackIcon ?? s?.icon ?? "?";
}

export function synergyFallbackIcon(synergyPassiveId) {
  const key = synergyBarIconKey(synergyPassiveId);
  return (key && SYNERGY_FALLBACK_ICONS[key]) || "✦";
}

export function barFallbackIcon(spellId, synergyPassiveId) {
  return synergyPassiveId ? synergyFallbackIcon(synergyPassiveId) : spellFallbackIcon(spellId);
}

export function isSpellIconReady(spellId) {
  return !!_ready[spellId];
}

function markReady(id) {
  _ready[id] = true;
}

function preloadIcons(assets) {
  for (const [id, src] of Object.entries(assets)) {
    const img = new Image();
    img.onload = () => markReady(id);
    img.onerror = () => { delete _ready[id]; };
    _images[id] = img;
    img.src = src;
    if (img.decode) img.decode().then(() => markReady(id)).catch(() => {});
  }
}

function preloadSpellIcons() {
  preloadIcons(SPELL_ICON_ASSETS);
  preloadIcons(SYNERGY_ICON_ASSETS);
}
preloadSpellIcons();

/** innerHTML for icon wells (.spell-icon, .mm-spell-entry-icon, .tip-icon, …) */
export function spellIconInner(spellId, alt) {
  const src = SPELL_ICON_ASSETS[spellId];
  if (!src) return spellFallbackIcon(spellId);
  const name = alt || SPELLS[spellId]?.name || spellId;
  return `<img class="spell-icon-img" src="${src}" alt="" aria-hidden="true" draggable="false" title="${name}">`;
}

export function synergyIconInner(synergyPassiveId, alt) {
  const key = synergyBarIconKey(synergyPassiveId);
  const src = key && SYNERGY_ICON_ASSETS[key];
  if (!src) return synergyFallbackIcon(synergyPassiveId);
  const name = alt || synergyPassiveId;
  return `<img class="spell-icon-img" src="${src}" alt="" aria-hidden="true" draggable="false" title="${esc(name)}">`;
}

/** HTML for a synergy icon inside a passive-node orb (counter-rotates the diamond). */
export function synergyOrbInner(synergyPassiveId, alt) {
  return `<span class="synergy-orb-icon">${synergyIconInner(synergyPassiveId, alt)}</span>`;
}

export function barIconInner(spellId, synergyPassiveId, alt) {
  if (synergyPassiveId) return synergyIconInner(synergyPassiveId, alt);
  return spellIconInner(spellId, alt);
}

/** inline HTML: small icon well + spell name (combat report, sandbox, match stats, …) */
export function spellLabelHTML(spellId, nameOverride, icClass = "spell-inline-ic") {
  const s = SPELLS[spellId];
  const name = nameOverride ?? s?.name ?? spellId;
  const color = s?.color || "#8a86a6";
  return `<span class="spell-inline-label"><span class="${icClass}" style="background:${color}22;border:1px solid ${color}">${spellIconInner(spellId)}</span>${esc(name)}</span>`;
}

/** Canvas HUD / spell bar — returns true if a loaded asset was drawn */
export function drawSpellIcon(c, cx, cy, size, iconKey) {
  if (!_ready[iconKey]) return false;
  const img = _images[iconKey];
  if (!img) return false;
  const pad = size * 0.12;
  const s = size - pad * 2;
  c.drawImage(img, cx - s / 2, cy - s / 2, s, s);
  return true;
}

/** Canvas action bar — draws synergy or base spell icon, else fallback emoji. */
export function drawBarIcon(c, cx, cy, size, spellId, synergyPassiveId) {
  const key = barIconKey(spellId, synergyPassiveId);
  if (drawSpellIcon(c, cx, cy, size, key)) return true;
  const fb = barFallbackIcon(spellId, synergyPassiveId);
  c.font = "24px sans-serif";
  c.textAlign = "center";
  c.textBaseline = "middle";
  c.fillText(fb, cx, cy);
  return false;
}
